import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiUrl } from "../lib/apiBase";
import { openExternalUrl } from "../lib/appMode";

function getToken() {
  return (
    localStorage.getItem("ga_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    ""
  );
}

async function request(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    cache: "no-store",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

function money(cents) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(cents || 0) / 100);
}

function dateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function StatusPill({ children, tone = "neutral" }) {
  return <span className={`gap-pill gap-pill--${tone}`}>{children}</span>;
}

export default function AdminPromotion() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [manager, setManager] = useState(null);
  const [inviteRequired, setInviteRequired] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await request("/admin-promotions/me");
      setDashboard(data);
      setInviteRequired(false);
      if (data?.isFounder) {
        const adminData = await request("/admin-promotions/admin/manage");
        setManager(adminData);
      }
    } catch (err) {
      if (err.status === 403 && err.data?.error === "admin_invite_required") {
        setInviteRequired(true);
      } else {
        setError(err.message || "Could not load the admin promotion program.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("connect") === "return") {
      setNotice("Stripe returned you to GAStreaming. Refreshing your payout status now.");
      load();
    }
  }, [location.search, load]);

  const participant = dashboard?.participant;
  const totals = dashboard?.totals || {};
  const config = dashboard?.config || manager?.config || {};
  const payoutReady = Boolean(dashboard?.user?.stripeConnect?.payoutsEnabled && dashboard?.user?.stripeConnect?.detailsSubmitted);
  const progress = Math.min(100, (Number(totals.approvedSignups || 0) / Number(config.signupRewardLimit || 10)) * 100);

  async function acceptTerms() {
    setBusy("accept");
    setError("");
    try {
      const data = await request("/admin-promotions/accept", { method: "POST", body: { acceptTerms: true } });
      setDashboard(data);
      setNotice("You are enrolled. Your personal referral link is ready.");
      if (data?.isFounder) setManager(await request("/admin-promotions/admin/manage"));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  }

  async function copyText(value, label) {
    try {
      await navigator.clipboard.writeText(value);
      setNotice(`${label} copied.`);
    } catch {
      window.prompt(`Copy ${label.toLowerCase()}:`, value);
    }
  }

  async function startStripe() {
    setBusy("stripe");
    setError("");
    try {
      const data = await request("/connect/onboard", {
        method: "POST",
        body: { returnPath: "/admin/promotion" },
      });
      if (!data?.url) throw new Error("Stripe payout setup did not return a link.");
      await openExternalUrl(data.url);
    } catch (err) {
      setError(err.message || "Could not start Stripe payout setup.");
      setBusy("");
    }
  }

  async function searchUsers(event) {
    event?.preventDefault();
    if (!search.trim()) return;
    setBusy("search");
    setError("");
    try {
      const data = await request(`/admin-promotions/admin/users?q=${encodeURIComponent(search.trim())}`);
      setSearchResults(data?.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  }

  async function assignUser(user) {
    const label = `${user.username || "Unnamed"} (${user.email})`;
    if (!window.confirm(`Grant ${label} the same limited tournament-dispute admin access as Lost and Deqevious, and invite them to this promotion program?`)) return;
    setBusy(`assign:${user.id}`);
    setError("");
    try {
      const data = await request("/admin-promotions/admin/participants", { method: "POST", body: { userId: user.id } });
      setNotice(data?.delivery?.complete
        ? `${label} was added and their email/site/app invitation was sent.`
        : `${label} was added, but one or more invitation channels need a retry. Use Resend invite.`);
      setManager(await request("/admin-promotions/admin/manage"));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  }

  async function sendInvitations(participantId = "") {
    setBusy(participantId ? `invite:${participantId}` : "invite-all");
    setError("");
    try {
      const data = await request("/admin-promotions/admin/invitations/send", {
        method: "POST",
        body: participantId ? { participantId } : {},
      });
      setNotice(data.failed
        ? `${data.sent || 0} invitation${data.sent === 1 ? "" : "s"} fully delivered; ${data.failed} need a retry.`
        : `${data.sent || 0} invitation${data.sent === 1 ? "" : "s"} delivered by email plus the site/app notification system.`);
      setManager(await request("/admin-promotions/admin/manage"));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  }

  async function reviewReferral(referral, action) {
    const note = action === "reject" ? window.prompt("Reason for rejection:", "Duplicate, fake, or unqualified account") : "Email verified and founder-approved engagement";
    if (action === "reject" && note === null) return;
    setBusy(`referral:${referral.id}`);
    setError("");
    try {
      await request(`/admin-promotions/admin/referrals/${encodeURIComponent(referral.id)}/review`, {
        method: "POST",
        body: { action, note },
      });
      setNotice(`Referral ${action === "approve" ? "approved" : "rejected"}.`);
      setManager(await request("/admin-promotions/admin/manage"));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  }

  async function approveBonus(candidate) {
    if (!candidate.fullParticipationVerified) return;
    if (!window.confirm(`Approve the one-time $25 tournament bonus for ${candidate.teamName}?`)) return;
    setBusy(`bonus:${candidate.participantId}`);
    setError("");
    try {
      await request("/admin-promotions/admin/tournament-bonuses", {
        method: "POST",
        body: candidate,
      });
      setNotice("The $25 tournament participation bonus was approved.");
      setManager(await request("/admin-promotions/admin/manage"));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  }

  async function recordPayout(summary) {
    if (!window.confirm("Only continue after you have actually sent this payment through Stripe. This records it as paid; it does not move money automatically.")) return;
    const note = window.prompt("Stripe transfer/payment note or reference (recommended):", "") || "";
    setBusy(`payout:${summary.participant.id}`);
    setError("");
    try {
      await request(`/admin-promotions/admin/payouts/${encodeURIComponent(summary.participant.id)}/record`, {
        method: "POST",
        body: { note },
      });
      setNotice("Payout was recorded as paid. No automatic transfer was made by GAStreaming.");
      setManager(await request("/admin-promotions/admin/manage"));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  }

  const pendingReferrals = useMemo(() =>
    (manager?.participants || []).flatMap((summary) =>
      (summary.referrals || [])
        .filter((referral) => referral.status === "pending_review")
        .map((referral) => ({ ...referral, promoter: summary.participant }))
    ), [manager]
  );

  return (
    <main className="gap-page">
      <style>{`
        .gap-page{max-width:1240px;margin:0 auto;padding:20px 16px 80px;color:#fff}.gap-hero,.gap-card{border:1px solid rgba(255,66,77,.28);background:linear-gradient(145deg,rgba(57,7,13,.88),rgba(7,7,9,.94));box-shadow:0 20px 60px rgba(0,0,0,.35)}.gap-hero{padding:clamp(22px,4vw,42px);border-radius:26px}.gap-card{border-radius:20px;padding:20px}.gap-eyebrow{color:#ff9aa2;font-size:12px;font-weight:900;letter-spacing:.15em}.gap-title{font-size:clamp(2.2rem,7vw,5rem);line-height:.95;margin:8px 0 12px}.gap-copy{color:rgba(255,255,255,.74);line-height:1.6;max-width:850px}.gap-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:14px;margin-top:16px}.gap-stat{border:1px solid rgba(255,255,255,.1);border-radius:16px;padding:16px;background:rgba(255,255,255,.04)}.gap-stat strong{display:block;font-size:1.8rem;margin-top:4px}.gap-actions{display:flex;flex-wrap:wrap;gap:10px;margin-top:16px}.gap-btn{border:1px solid rgba(255,255,255,.16);border-radius:999px;background:rgba(255,255,255,.07);color:#fff;padding:11px 16px;font-weight:900;cursor:pointer;text-decoration:none}.gap-btn--primary{background:linear-gradient(135deg,#e11d2e,#79030c);border-color:rgba(255,82,92,.7)}.gap-btn--green{background:linear-gradient(135deg,#168448,#07562c);border-color:rgba(80,230,137,.45)}.gap-btn:disabled{opacity:.5;cursor:not-allowed}.gap-section{margin-top:20px}.gap-section h2{font-size:clamp(1.6rem,4vw,2.5rem);margin:4px 0 12px}.gap-alert{margin-top:14px;border-radius:14px;padding:13px 15px}.gap-alert--error{background:rgba(132,13,22,.4);border:1px solid rgba(255,76,88,.38);color:#ffc1c5}.gap-alert--ok{background:rgba(12,105,52,.35);border:1px solid rgba(75,223,132,.3);color:#baf6cf}.gap-rules{margin:14px 0 0;padding-left:20px;color:rgba(255,255,255,.82);line-height:1.75}.gap-linkbox{display:flex;gap:10px;align-items:center;margin-top:12px}.gap-linkbox input,.gap-input{min-width:0;flex:1;border-radius:12px;border:1px solid rgba(255,255,255,.15);background:#0b0b0c;color:#fff;padding:12px;font:inherit}.gap-progress{height:11px;border-radius:999px;background:rgba(255,255,255,.1);overflow:hidden;margin-top:10px}.gap-progress span{display:block;height:100%;background:linear-gradient(90deg,#e51b2e,#ffc34d)}.gap-pill{display:inline-flex;border-radius:999px;padding:6px 9px;font-size:11px;font-weight:900;background:rgba(255,255,255,.09);border:1px solid rgba(255,255,255,.12)}.gap-pill--good{color:#aaf4c4;background:rgba(19,112,54,.3)}.gap-pill--warn{color:#ffdc98;background:rgba(130,75,8,.32)}.gap-pill--bad{color:#ffb4ba;background:rgba(130,13,22,.34)}.gap-list{display:grid;gap:10px}.gap-row{border:1px solid rgba(255,255,255,.09);background:rgba(255,255,255,.035);border-radius:14px;padding:14px;display:flex;justify-content:space-between;gap:14px;align-items:center;flex-wrap:wrap}.gap-meta{color:rgba(255,255,255,.6);font-size:13px;line-height:1.5}.gap-table{width:100%;border-collapse:collapse}.gap-table th,.gap-table td{text-align:left;padding:11px 8px;border-bottom:1px solid rgba(255,255,255,.08);vertical-align:top}.gap-table th{color:rgba(255,255,255,.48);font-size:11px;letter-spacing:.1em}.gap-scroll{overflow-x:auto}.gap-manager{border-color:rgba(255,194,76,.35);background:linear-gradient(145deg,rgba(66,37,4,.52),rgba(7,7,9,.95))}@media(max-width:640px){.gap-linkbox{align-items:stretch;flex-direction:column}.gap-linkbox input{width:100%}.gap-table{min-width:720px}}
      `}</style>

      <section className="gap-hero">
        <div className="gap-eyebrow">PRIVATE ADMIN INCENTIVE</div>
        <h1 className="gap-title">Get Active. Bring the community.</h1>
        <p className="gap-copy">Eligible GAStreaming admins can earn for verified growth while every signup and tournament claim remains founder-reviewed. Tracking can begin now; the first payout date is August 31, 2026.</p>
        <div className="gap-actions"><Link className="gap-btn" to="/tournaments">View tournament</Link><Link className="gap-btn" to="/">GAStreaming home</Link></div>
      </section>

      {notice ? <div className="gap-alert gap-alert--ok">{notice}</div> : null}
      {error ? <div className="gap-alert gap-alert--error">{error}</div> : null}

      {loading ? <section className="gap-card gap-section">Loading your private promotion dashboard…</section> : null}

      {!loading && inviteRequired ? (
        <section className="gap-card gap-section">
          <div className="gap-eyebrow">INVITE REQUIRED</div>
          <h2>This program is not public.</h2>
          <p className="gap-copy">Sign in with the exact GAStreaming account the founder invited. A Discord link opens this page, but it cannot grant admin access by itself.</p>
        </section>
      ) : null}

      {!loading && participant ? (
        <>
          {participant.status !== "active" ? (
            <section className="gap-card gap-section">
              <div className="gap-eyebrow">ACCEPT PROGRAM RULES</div>
              <h2>Your admin invitation is ready.</h2>
              <ul className="gap-rules">
                <li>$5 for each founder-approved, email-verified real account; maximum 10 paid signups / $50.</li>
                <li>Fake, duplicate, self-referred, automated, or inactive accounts do not qualify.</li>
                <li>A separate one-time $25 bonus requires a real tournament team and verified full participation. Your own team only qualifies when you personally participate.</li>
                <li>Rewards accrue now. Nothing is payable before August 31, 2026, and Stripe onboarding is required.</li>
              </ul>
              <button className="gap-btn gap-btn--primary" disabled={busy === "accept"} onClick={acceptTerms}>{busy === "accept" ? "Enrolling…" : "I accept — activate my link"}</button>
            </section>
          ) : (
            <>
              <section className="gap-grid">
                <div className="gap-stat"><div className="gap-eyebrow">APPROVED SIGNUPS</div><strong>{totals.approvedSignups || 0} / {config.signupRewardLimit || 10}</strong><div className="gap-progress"><span style={{ width: `${progress}%` }} /></div></div>
                <div className="gap-stat"><div className="gap-eyebrow">PENDING REVIEW</div><strong>{totals.pendingSignups || 0}</strong><div className="gap-meta">Email-verified accounts awaiting founder review</div></div>
                <div className="gap-stat"><div className="gap-eyebrow">TOTAL APPROVED</div><strong>{money(totals.totalEarnedCents)}</strong><div className="gap-meta">Signup cap + separate tournament bonus</div></div>
                <div className="gap-stat"><div className="gap-eyebrow">PAYOUT STATUS</div><strong>{payoutReady ? "Ready" : "Setup needed"}</strong><div className="gap-meta">First payout: August 31, 2026</div></div>
              </section>

              <section className="gap-card gap-section">
                <div className="gap-eyebrow">YOUR PERSONAL LINK</div><h2>Share this for account signups</h2>
                <div className="gap-linkbox"><input readOnly value={dashboard.shareUrl || ""} /><button className="gap-btn gap-btn--primary" onClick={() => copyText(dashboard.shareUrl, "Referral link")}>Copy link</button></div>
                <p className="gap-meta">The referral is saved before the new member verifies their email, then enters founder review after verification.</p>
              </section>

              <section className="gap-card gap-section">
                <div className="gap-eyebrow">STRIPE PAYOUT ONBOARDING</div><h2>{payoutReady ? "Your payout account is connected." : "Connect before payout day."}</h2>
                <p className="gap-copy">The same Stripe Connect account is reused for tournament payouts and GAS Cheers. GAStreaming will not create a duplicate account.</p>
                {!payoutReady ? <button className="gap-btn gap-btn--green" disabled={busy === "stripe"} onClick={startStripe}>{busy === "stripe" ? "Opening Stripe…" : "Set up Stripe payouts"}</button> : <StatusPill tone="good">PAYOUTS ENABLED</StatusPill>}
              </section>

              <section className="gap-card gap-section">
                <div className="gap-eyebrow">YOUR TRACKING HISTORY</div><h2>Referrals and rewards</h2>
                {(dashboard.referrals || []).length ? <div className="gap-list">{dashboard.referrals.map((referral) => <div className="gap-row" key={referral.id}><div><strong>{referral.referredUsername || "New member"}</strong><div className="gap-meta">Created {dateTime(referral.createdAt)}</div></div><div><StatusPill tone={referral.status === "approved" || referral.status === "paid" ? "good" : referral.status === "rejected" ? "bad" : "warn"}>{String(referral.status).replaceAll("_", " ").toUpperCase()}</StatusPill> <strong>{money(referral.amountCents)}</strong></div></div>)}</div> : <p className="gap-copy">No verified referral signups yet.</p>}
              </section>
            </>
          )}
        </>
      ) : null}

      {dashboard?.isFounder && manager ? (
        <section className="gap-card gap-manager gap-section">
          <div className="gap-eyebrow">FOUNDER CONTROL CENTER</div><h2>Admin invitations, fraud review, and payouts</h2>
          <p className="gap-copy">The program never grants access from a Discord link alone and never moves money automatically. Select the exact production account, review every signup, then record a payout only after you actually send it through Stripe.</p>

          <div className="gap-section"><h3>Private Discord onboarding link</h3><div className="gap-linkbox"><input readOnly value={manager.discordInviteUrl || dashboard.discordInviteUrl || ""} /><button className="gap-btn" onClick={() => copyText(manager.discordInviteUrl || dashboard.discordInviteUrl, "Discord invite link")}>Copy for Discord</button></div></div>

          <div className="gap-section"><h3>Find Korupt or the correct Deqevious account</h3><form className="gap-linkbox" onSubmit={searchUsers}><input className="gap-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search exact username or email" /><button className="gap-btn" disabled={busy === "search"}>{busy === "search" ? "Searching…" : "Search users"}</button></form><div className="gap-list" style={{ marginTop: 10 }}>{searchResults.map((user) => <div className="gap-row" key={user.id}><div><strong>{user.username || "Unnamed"}</strong><div className="gap-meta">{user.email} • Account {user.id}</div></div><button className="gap-btn gap-btn--primary" disabled={busy === `assign:${user.id}`} onClick={() => assignUser(user)}>Add limited admin + invite</button></div>)}</div></div>

          <div className="gap-section"><div className="gap-actions" style={{ justifyContent: "space-between" }}><h3>Eligible admins</h3><button className="gap-btn" disabled={busy === "invite-all"} onClick={() => sendInvitations("")}>Send all unsent invitations</button></div><div className="gap-scroll"><table className="gap-table"><thead><tr><th>ADMIN</th><th>STATUS</th><th>SIGNUPS</th><th>UNPAID</th><th>STRIPE</th><th>ACTION</th></tr></thead><tbody>{(manager.participants || []).map((summary) => <tr key={summary.participant.id}><td><strong>{summary.participant.username || "Unnamed"}</strong><br/><span className="gap-meta">{summary.participant.email}</span></td><td><StatusPill tone={summary.participant.status === "active" ? "good" : "warn"}>{summary.participant.status.toUpperCase()}</StatusPill></td><td>{summary.totals.approvedSignups}/10 approved<br/><span className="gap-meta">{summary.totals.pendingSignups} pending</span></td><td>{money(summary.totals.unpaidCents)}</td><td>{summary.user?.stripeConnect?.payoutsEnabled ? <StatusPill tone="good">READY</StatusPill> : <StatusPill tone="warn">NOT READY</StatusPill>}</td><td><div className="gap-actions"><button className="gap-btn" onClick={() => sendInvitations(summary.participant.id)}>Resend invite</button><button className="gap-btn gap-btn--green" disabled={config.payoutsLocked || !summary.totals.unpaidCents || !summary.user?.stripeConnect?.payoutsEnabled || busy === `payout:${summary.participant.id}`} onClick={() => recordPayout(summary)}>Record paid</button></div></td></tr>)}</tbody></table></div></div>

          <div className="gap-section"><h3>Pending $5 signup reviews</h3>{pendingReferrals.length ? <div className="gap-list">{pendingReferrals.map((referral) => <div className="gap-row" key={referral.id}><div><strong>{referral.referredUsername}</strong> via {referral.promoter.username}<div className="gap-meta">Email verified • {dateTime(referral.createdAt)} • {referral.fraudReview?.sharedIpSignal || referral.fraudReview?.sharedDeviceSignal ? "⚠️ shares an IP/device signal with another referral — inspect before approval" : "no duplicate IP/device signal detected"}</div></div><div className="gap-actions"><button className="gap-btn gap-btn--green" disabled={busy === `referral:${referral.id}`} onClick={() => reviewReferral(referral, "approve")}>Approve $5</button><button className="gap-btn" disabled={busy === `referral:${referral.id}`} onClick={() => reviewReferral(referral, "reject")}>Reject</button></div></div>)}</div> : <p className="gap-copy">No signup claims need review.</p>}</div>

          <div className="gap-section"><h3>One-time $25 tournament candidates</h3>{(manager.tournamentCandidates || []).length ? <div className="gap-list">{manager.tournamentCandidates.map((candidate) => <div className="gap-row" key={`${candidate.participantId}:${candidate.teamId}`}><div><strong>{candidate.teamName}</strong><div className="gap-meta">{candidate.qualificationType.replaceAll("_", " ")} • {candidate.fullParticipationVerified ? "full participation verified" : "waiting for completed-match evidence"}</div></div><button className="gap-btn gap-btn--green" disabled={!candidate.fullParticipationVerified || busy === `bonus:${candidate.participantId}`} onClick={() => approveBonus(candidate)}>Approve $25</button></div>)}</div> : <p className="gap-copy">No eligible tournament team has verified full participation yet.</p>}</div>
        </section>
      ) : null}
    </main>
  );
}
